import { getLookupData } from '@/services/lookup';

import type { TGetLookups, TLookup } from '@/types/lookup';

import { stableParams, unwrapData } from './utils';

/**
 * Keys of the lookup area (the backend's reference lists: brands, amenities, terms, portal
 * defaults). Every query of this area starts with `lookupKeys.all`.
 * Invalidated by: nothing. Lookups never change during a session, so they are never refreshed
 * until sign-out (`queryClient.clear()` in `logout()`).
 */
export const lookupKeys = {
  all: ['lookup'] as const,
  list: (params: Parameters<TGetLookups>[0]) =>
    [...lookupKeys.all, 'list', stableParams(params)] as const,
};

/**
 * Query for one lookup category (`{ category: 'brands' }`, `{ category: 'partner_portal' }`, ...).
 * What: resolves to the list of lookup rows (`response.data.data`, same shape `useCached` gave).
 * Why `staleTime: Infinity`: lookups are static reference data; refetching on focus or after the
 * fresh window would only cost calls. They load once per session.
 * Key: `['lookup', 'list', params]`, so each category is cached on its own.
 * Constraint: the returned rows are shared by every caller. Do NOT sort or change them in place;
 * sort a copy (see `useBrands`).
 * Invalidated by: no save.
 *
 * @param params Lookup filter (`category`, optional `type` and `key`).
 */
export const lookupQuery = <PCategory extends TLookup['category']>(
  params: Omit<Parameters<TGetLookups>[0], 'category'> & { category?: PCategory },
) => {
  const stable = stableParams(params);

  return {
    queryKey: lookupKeys.list(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getLookupData<PCategory>(stable, signal).then((response) => unwrapData(response.data.data)),
    staleTime: Infinity,
  };
};
